import { useEffect, useState } from "react";

import {
  clearDeferredInstallEvent,
  getDeferredInstallEvent,
  subscribeInstallEvent,
  type BeforeInstallPromptEvent,
} from "../../pwa/installEvent";
import {
  markInstallPromptDismissed,
  shouldShowInstallPrompt,
  type KeyValueStorage,
} from "../../pwa/installPromptState";
import { isIos, isStandaloneDisplay } from "../../pwa/platform";

/** localStorage puede lanzar (SecurityError con datos de sitio bloqueados). */
function safeLocalStorage(): KeyValueStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Ícono «Compartir» de iPhone (cuadrito con flecha hacia arriba), para que
 * la gente reconozca el botón en Safari (Eduardo 2026-10-01). */
function IosShareIcon() {
  return (
    <svg
      className="pwa-install-card__share-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="Compartir"
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M8 11H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2" />
    </svg>
  );
}

export default function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(
    getDeferredInstallEvent,
  );
  const [storage] = useState<KeyValueStorage | null>(safeLocalStorage);
  const [visible, setVisible] = useState(() => {
    if (!storage) return false;
    try {
      return shouldShowInstallPrompt(storage, Date.now());
    } catch {
      return false;
    }
  });
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => subscribeInstallEvent(setInstallEvent), []);

  const standalone = isStandaloneDisplay(
    window.matchMedia("(display-mode: standalone)").matches,
    (navigator as Navigator & { standalone?: unknown }).standalone,
  );
  const ios = isIos(navigator.userAgent, navigator.maxTouchPoints);

  // Instalada, sin storage utilizable, descartada hace <30 días, o navegador
  // sin instalación posible.
  if (standalone || !visible) return null;
  if (!ios && !installEvent) return null;

  const dismiss = () => {
    if (storage) {
      try {
        markInstallPromptDismissed(storage, Date.now());
      } catch {
        // Cuota llena o storage deshabilitado: solo ocultamos este montaje.
      }
    }
    setVisible(false);
  };

  const install = async () => {
    if (installEvent) {
      try {
        await installEvent.prompt();
      } catch {
        // Evento ya consumido o bloqueado por el navegador: tratamos igual
        // que un descarte para no dejar la tarjeta con un evento muerto.
      }
      clearDeferredInstallEvent();
      dismiss();
      return;
    }
    setShowIosSteps(true);
  };

  return (
    <div className="pwa-install-card" role="region" aria-label="Instalar Torneos123">
      <span className="pwa-install-card__text">
        Instala la Aplicación de Torneos123 en tu celular para recibir todas las notificaciones de tus
        torneos en tiempo real.
      </span>
      <button
        type="button"
        className="btn btn--primary"
        onClick={() => void install()}
        aria-expanded={showIosSteps}
        aria-controls="pwa-install-ios-steps"
      >
        Instalar App
      </button>
      <button type="button" className="btn btn--secondary" onClick={dismiss}>
        Ahora no
      </button>
      {showIosSteps ? (
        <ol id="pwa-install-ios-steps" className="pwa-install-card__steps">
          <li>
            Toca el botón Compartir <IosShareIcon /> de tu navegador (arriba o abajo de la pantalla).
          </li>
          <li>Elige "Agregar a pantalla de inicio" y confirma.</li>
        </ol>
      ) : null}
    </div>
  );
}
